/**
 * BillingOverviewPage - Platform admin billing overview dashboard
 *
 * Features:
 * - System-wide revenue metrics (MRR, ARR, churn)
 * - All-tenant billing overview
 * - Quick access to plans and settings
 * - Recent billing activity
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  Users,
  CreditCard,
  AlertCircle,
  Settings,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Navigate, Link } from 'react-router-dom';
import { RevenueDashboard } from '@/components/admin/RevenueDashboard';

interface BillingOverview {
  total_tenants: number;
  active_subscriptions: number;
  mrr: number;
  arr: number;
  churn_rate: number;
  avg_revenue_per_tenant: number;
}

export default function BillingOverviewPage() {
  const { isAdmin, loading: authLoading } = useAuth();

  // Fetch billing overview metrics
  const { data: overview, isLoading, error } = useQuery({
    queryKey: ['admin-billing-overview'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_get_billing_overview');
      if (error) throw error;
      // RPC returns array, get first row
      const row = Array.isArray(data) ? data[0] : data;
      return row as BillingOverview;
    },
    enabled: !authLoading && isAdmin(),
  });

  // Redirect non-admins
  if (!authLoading && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                <DollarSign className="h-8 w-8 text-primary" />
                Platform Billing
              </h1>
              <p className="text-muted-foreground mt-1">
                System-wide billing administration and revenue metrics
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline">
                <Link to="/admin/billing/settings">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </Link>
              </Button>
              <Button asChild>
                <Link to="/admin/billing/plans">
                  <FileText className="h-4 w-4 mr-2" />
                  Manage Plans
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Card className="mb-6 border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p>Failed to load billing data: {(error as Error).message}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* MRR Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Recurring Revenue</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{formatCurrency(overview?.mrr || 0)}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {overview?.active_subscriptions || 0} active subscriptions
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* ARR Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Annual Recurring Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{formatCurrency(overview?.arr || 0)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Projected annual revenue</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Total Tenants Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{overview?.total_tenants || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Avg: {formatCurrency(overview?.avg_revenue_per_tenant || 0)}/mo per tenant
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Churn Rate Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold">
                      {formatPercentage(overview?.churn_rate || 0)}
                    </div>
                    {(overview?.churn_rate || 0) < 5 ? (
                      <ArrowDownRight className="h-5 w-5 text-green-600" />
                    ) : (
                      <ArrowUpRight className="h-5 w-5 text-destructive" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(overview?.churn_rate || 0) < 5 ? 'Healthy churn' : 'Needs attention'}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Revenue Dashboard */}
        <RevenueDashboard />
      </div>
    </MainLayout>
  );
}
